import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config';
import PrescriptionForm from './PrescriptionForm';
import XaiPromptPanel from '../XaiPromptPanel';
import { CitationChip, CitationsBlock } from '../Citations';
import EvidenceWarnings from '../EvidenceWarnings';
import MedicalDisclaimer from '../MedicalDisclaimer';
import { handleAIError } from '../../utils/aiErrorHandler';
import CostChip from '../CostChip';
import { useCreditSpend } from '../../contexts/CreditSpendContext';
import { useConfirm } from '../ConfirmProvider';

function MedicationsPage({ fetchWithAuth, setActiveTab }) {
  const confirm = useConfirm();
  const { spend } = useCreditSpend();  // #77 supplement_recommendations = 3 credits (chip + low-balance confirm)
  const [prescriptions, setPrescriptions] = useState([]);
  const [showPrescriptionForm, setShowPrescriptionForm] = useState(false);
  const [editingPrescription, setEditingPrescription] = useState(null);
  const [medSubTab, setMedSubTab] = useState('prescriptions');
  const [interactions, setInteractions] = useState(null);
  const [interactionsLoading, setInteractionsLoading] = useState(false);
  const [timingData, setTimingData] = useState(null);
  const [timingLoading, setTimingLoading] = useState(false);
  const [defaultItemType, setDefaultItemType] = useState('prescription');

  useEffect(() => {
    loadPrescriptions();
  }, []);

  useEffect(() => {
    if (medSubTab === 'interactions') {
      loadInteractions();
      loadTiming();
    }
  }, [medSubTab]);

  const loadPrescriptions = async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/prescriptions/`);
      const data = await response.json();
      setPrescriptions(data);
    } catch (error) {
      console.error('Failed to load prescriptions:', error);
    }
  };

  const loadInteractions = async () => {
    setInteractionsLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/prescriptions/interactions`);
      // A failed request must NOT render as "No interactions detected" — that is a false
      // all-clear on a safety screen. Non-OK → null → "No interaction data available".
      if (!response.ok) {
        console.error('Failed to load interactions: HTTP', response.status);
        setInteractions(null);
        return;
      }
      const data = await response.json();
      setInteractions(data);
    } catch (error) {
      console.error('Failed to load interactions:', error);
      setInteractions(null);
    } finally {
      setInteractionsLoading(false);
    }
  };

  const loadTiming = async () => {
    setTimingLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/prescriptions/timing`);
      if (!response.ok) {
        console.error('Failed to load timing: HTTP', response.status);
        setTimingData(null);
        return;
      }
      const data = await response.json();
      setTimingData(data);
    } catch (error) {
      console.error('Failed to load timing:', error);
    } finally {
      setTimingLoading(false);
    }
  };

  const handleEditPrescription = (prescription) => {
    setEditingPrescription(prescription);
    setShowPrescriptionForm(true);
  };

  const handleDeletePrescription = async (prescriptionId) => {
    // Deliberately names the record type — a medication removal changes what the UHC
    // reasons over (interactions, timing, food-drug checks), so "this item" was too vague.
    if (await confirm({
      title: 'Delete this medication?',
      message: 'It will be removed from your medication list, and Healist will stop factoring it into interaction, timing and food-safety checks.',
      confirmLabel: 'Delete',
      destructive: true,
    })) {
      try {
        const response = await fetchWithAuth(`${API_BASE}/prescriptions/${prescriptionId}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          loadPrescriptions();
        }
      } catch (error) {
        console.error('Failed to delete prescription:', error);
      }
    }
  };

  // Migration 137: the user's decision on a medication imported from their health records.
  const handleReview = async (prescriptionId, stillTaking) => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/prescriptions/${prescriptionId}/review`, {
        method: 'POST',
        body: JSON.stringify({ still_taking: stillTaking })
      });
      if (response.ok) {
        loadPrescriptions();
      } else {
        console.error('Medication review failed: HTTP', response.status);
      }
    } catch (error) {
      console.error('Medication review failed:', error);
    }
  };

  const closePrescriptionForm = () => {
    setShowPrescriptionForm(false);
    setEditingPrescription(null);
    setPrefillData(null);
    setDefaultItemType('prescription');
  };

  const handleAddNew = (itemType) => {
    setDefaultItemType(itemType);
    setEditingPrescription(null);
    setShowPrescriptionForm(true);
  };

  const [showAiRecs, setShowAiRecs] = useState(false);
  const [aiRecs, setAiRecs] = useState(null);
  const [aiRecsLoading, setAiRecsLoading] = useState(false);
  const [aiRecsError, setAiRecsError] = useState(null);
  const [aiRecsPromptData, setAiRecsPromptData] = useState(null);
  const [aiRecsNote, setAiRecsNote] = useState(null);

  const loadAiRecommendations = async () => {
    setShowAiRecs(true);
    setAiRecsLoading(true);
    setAiRecsError(null);
    try {
      const response = await fetchWithAuth(`${API_BASE}/prescriptions/ai-recommendations`, {
        method: 'POST'
      });
      if (await handleAIError(response)) {
        setShowAiRecs(false);
        return;
      }
      if (!response.ok) throw new Error('AI recommendation failed');
      const data = await response.json();
      setAiRecs(data.recommendations || []);
      setAiRecsPromptData(data.prompt_data || null);
      setAiRecsNote(data.catalog_note || null);
    } catch (error) {
      console.error('Failed to get AI recommendations:', error);
      setAiRecsError('Failed to generate recommendations. Please try again.');
    } finally {
      setAiRecsLoading(false);
    }
  };

  const [prefillData, setPrefillData] = useState(null);

  const handleAddFromRec = (rec) => {
    // F-15: the DOSE is never pre-filled. A suggestion's intake figure is a TOTAL daily intake from all
    // sources (it can exceed the supplement upper limit — magnesium), and it used to save the model's own
    // made-up dose into the user's record, where every other tool then read it as theirs.
    setPrefillData({
      medication_name: rec.supplement_name,
      item_type: 'supplement',
      category: rec.category || '',
      dosage: '',
      timing_instructions: '',
      reason: rec.reason || '',
    });
    setEditingPrescription(null);
    setDefaultItemType('supplement');
    setShowAiRecs(false);
    setShowPrescriptionForm(true);
  };

  const rxItems = prescriptions.filter(p => p.item_type === 'prescription' || (!p.item_type && !p.category));
  const suppItems = prescriptions.filter(p => p.item_type === 'supplement' || p.item_type === 'otc');

  const severityIcon = (severity) => {
    switch(severity) {
      case 'critical': return '\uD83D\uDD34';
      case 'significant': return '\uD83D\uDFE0';
      case 'moderate': return '\uD83D\uDFE1';
      case 'minor': return '\uD83D\uDD35';
      case 'info': return '\uD83D\uDFE2';
      default: return '\u26AA';
    }
  };

  const severityLabel = (severity) => {
    return severity ? severity.charAt(0).toUpperCase() + severity.slice(1) : '';
  };

  const renderItemList = (items, emptyMsg, addLabel, addType) => (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">{addLabel}</h3>
        <button className="btn btn-primary btn-sm" onClick={() => handleAddNew(addType)}>
          Add {addLabel.replace(/s$/, '')}
        </button>
      </div>
      {items.length === 0 ? (
        <p className="med-emptyText">{emptyMsg}</p>
      ) : (
        <div>
          {items.map(item => (
            <div key={item.id} className="objective-item">
              <div className="objective-content">
                <h4>{item.medication_name}</h4>
                <p>{[item.dosage, item.frequency].filter(Boolean).join(' \u2014 ')}</p>
                {item.category && <p className="med-category">{item.category}</p>}
                {item.reason && <p className="med-reason">{item.reason}</p>}
                {item.timing_instructions && <p className="med-timing">{item.timing_instructions}</p>}
                {item.prescribing_doctor && <p>Prescribed by: {item.prescribing_doctor}</p>}
                <p>Status: {item.is_active ? 'Active' : 'Inactive'}</p>
                {item.needs_review && (
                  // Migration 137 (2026-11-02): imported from health records, not yet confirmed.
                  // It is included in interaction checks until the user decides.
                  <div className="med-reviewNote" role="note">
                    <strong>Imported — please review.</strong>{' '}
                    This came from your health records
                    {item.source_status ? ` (the record lists it as "${item.source_status}")` : ''}.
                    Until you confirm it, Healist includes it when checking foods and supplements for interactions.
                  </div>
                )}
              </div>
              <div className="objective-actions">
                {item.needs_review && (
                  <>
                    <button className="btn btn-primary btn-sm" onClick={() => handleReview(item.id, true)}>I still take this</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => handleReview(item.id, false)}>I don't take this</button>
                  </>
                )}
                <button className="btn btn-secondary btn-sm" onClick={() => handleEditPrescription(item)}>Edit</button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDeletePrescription(item.id)}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderInteractionsTab = () => {
    if (interactionsLoading) return <p>Loading interactions...</p>;
    // Degrade, never dead-end (Apple 1.4.1): if the interaction data could not be loaded, the
    // standards this check is built on are still one tap away.
    if (!interactions) return (
      <div className="card">
        <div className="card-header cite-header-row">
          <h3 className="card-title">Detected Interactions</h3>
          <CitationsBlock featureName="Medication Interaction Check" testId="interactions-citations" />
        </div>
        <p className="med-emptyText">No interaction data available.</p>
      </div>
    );

    const allInteractions = [
      ...(interactions.supplement_drug || []).map(i => ({ ...i, type: 'supplement_drug' })),
      ...(interactions.supplement_supplement || []).map(i => ({ ...i, type: 'supplement_supplement' })),
    ];

    const severityOrder = { critical: 0, significant: 1, moderate: 2, minor: 3, info: 4 };
    allInteractions.sort((a, b) => (severityOrder[a.severity] ?? 5) - (severityOrder[b.severity] ?? 5));

    return (
      <div>
        {/* Interaction Cards */}
        {/* Apple 1.4.1 (2026-10-17): this is the check Apple named ("interactions between a
            user's medications and the food or supplements"). One Citations control for the list,
            and a 📚 chip on every interaction with that interaction's own sources. */}
        <div className="card">
          <div className="card-header cite-header-row">
            <div>
              <h3 className="card-title">Detected Interactions</h3>
              {interactions.summary && <span className="med-interactionCount">{interactions.summary.total} found</span>}
            </div>
            <CitationsBlock
              sources={allInteractions.flatMap(ix => ix.sources || [])}
              featureName="Medication Interaction Check"
              itemTitle="your interaction check"
              testId="interactions-citations"
            />
          </div>
          {allInteractions.length === 0 ? (
            <p className="med-emptyText">No interactions detected between your current medications and supplements.</p>
          ) : (
            <div className="med-interactionList">
              {allInteractions.map((ix, idx) => {
                const pair = ix.type === 'supplement_drug'
                  ? `${ix.supplement} + ${ix.medication}`
                  : `${ix.supplement_a} + ${ix.supplement_b}`;
                return (
                  <div key={idx} className={`med-interactionCard med-severity-${ix.severity}`}>
                    <div className="med-interactionHeader">
                      <span className="med-severityBadge">{severityIcon(ix.severity)} {severityLabel(ix.severity)}</span>
                    </div>
                    <div className="med-interactionPair">
                      {ix.type === 'supplement_drug' ? (
                        <span><strong>{ix.supplement}</strong> + <strong>{ix.medication}</strong></span>
                      ) : (
                        <span><strong>{ix.supplement_a}</strong> + <strong>{ix.supplement_b}</strong></span>
                      )}
                    </div>
                    <p className="med-mechanism">{ix.mechanism}</p>
                    <p className="med-guidance">{ix.guidance}</p>
                    <CitationChip sources={ix.sources} featureName="Medication Interaction Check" itemTitle={pair} />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Timing Schedule */}
        <div className="card" style={{marginTop: '1rem'}}>
          <div className="card-header">
            <h3 className="card-title">Optimal Timing Schedule</h3>
          </div>
          {timingLoading ? (
            <p>Loading timing schedule...</p>
          ) : timingData && timingData.timing_groups && timingData.timing_groups.length > 0 ? (
            <div className="med-timingList">
              {timingData.timing_groups.map((group, idx) => (
                <div key={idx} className="med-timingGroup">
                  <h4 className="med-timingSlotLabel">{group.time_slot}</h4>
                  <ul className="med-timingItems">
                    {group.items.map((item, i) => (
                      <li key={i}>
                        {item.name}
                        {item.custom_timing && <span className="med-customTiming"> &mdash; {item.custom_timing}</span>}
                      </li>
                    ))}
                  </ul>
                  <p className="med-timingNotes">{group.notes}</p>
                  <CitationChip sources={group.sources} featureName="Medication Timing Schedule" itemTitle={group.time_slot} />
                </div>
              ))}
            </div>
          ) : (
            <p className="med-emptyText">Add medications and supplements to see your personalized timing schedule.</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="page-layout">
      <div className="page-sticky-nav">
        <div className="page-header-row">
          <h2 className="page-section-title">Medications</h2>
          <button className="page-exit-btn" onClick={() => setActiveTab('dashboard')}>&larr; Dashboard</button>
        </div>

        <div className="page-nav-bar">
          {/* ===== PILL TOOLBAR — Quick Action Tools ===== */}
          <div className="pill-toolbar">
            <button
              className="pill-btn"
              onClick={() => setMedSubTab('interactions')}
            >
              Check Interactions
            </button>
            <button
              className="pill-btn"
              onClick={() => spend('supplement_recommendations', 'Get AI supplement suggestions?', loadAiRecommendations)}
            >
              AI Supplement Suggestions <CostChip apiType="supplement_recommendations" />
            </button>
          </div>

          {/* 3-Tab Navigation */}
          <div className="tab-bar">
            <button
              className={`tab-item${medSubTab === 'prescriptions' ? ' tab-item--active' : ''}`}
              onClick={() => setMedSubTab('prescriptions')}
            >
              Prescriptions
            </button>
            <button
              className={`tab-item${medSubTab === 'supplements' ? ' tab-item--active' : ''}`}
              onClick={() => setMedSubTab('supplements')}
            >
              Supplements
            </button>
            <button
              className={`tab-item${medSubTab === 'interactions' ? ' tab-item--active' : ''}`}
              onClick={() => setMedSubTab('interactions')}
            >
              Interactions
            </button>
          </div>
        </div>
      </div>

      <div className="page-scroll-content">
      {medSubTab === 'prescriptions' && renderItemList(
        rxItems,
        'No prescriptions recorded. Add your first prescription!',
        'Prescriptions',
        'prescription'
      )}

      {medSubTab === 'supplements' && renderItemList(
        suppItems,
        'No supplements or OTC items recorded. Add your first supplement!',
        'Supplements & OTC',
        'supplement'
      )}

      {medSubTab === 'interactions' && renderInteractionsTab()}

      {/* AI Supplement Recommendations Modal */}
      {showAiRecs && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-lg">
            <div className="modal-header">
              <h3>AI Supplement Suggestions</h3>
              <button className="modal-close" onClick={() => setShowAiRecs(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {aiRecsLoading ? (
                <div className="med-aiLoading">
                  <p>Analyzing your health profile...</p>
                  <div className="spinner"></div>
                </div>
              ) : aiRecsError ? (
                <div className="med-aiError">
                  <p>{aiRecsError}</p>
                  <button className="btn btn-secondary btn-sm" onClick={loadAiRecommendations}>Retry</button>
                </div>
              ) : aiRecs && aiRecs.length > 0 ? (
                <>
                  {aiRecs.map((rec, idx) => (
                    <div key={idx} className={`med-recCard ${rec.has_interaction ? 'med-recCard--warning' : ''}`}>
                      <div className="med-recHeader">
                        <h4>{rec.supplement_name}</h4>
                        <span
                          className={`med-evidenceBadge med-evidence-${rec.evidence_level}`}
                          title={rec.evidence_level === 'strong' ? 'Multiple clinical trials support this' : rec.evidence_level === 'moderate' ? 'Some clinical evidence supports this' : 'Early or preliminary research'}
                        >
                          {rec.evidence_level}
                        </span>
                      </div>
                      {rec.category && <span className="med-category">{rec.category}</span>}
                      {/* F-15: the intake line is the catalog's CITED total daily intake (+ the supplement
                          upper limit), or "Discuss the dose with your clinician" — never a model dose. */}
                      <p className="med-recDosage" data-testid="supp-intake">{rec.dosage_suggestion}</p>
                      {rec.upper_limit && <p className="med-recUpperLimit" data-testid="supp-upper-limit">{rec.upper_limit}</p>}
                      {rec.reason && <p className="med-recReason">{rec.reason}</p>}
                      <EvidenceWarnings item={{ name: rec.supplement_name, evidence_sources: rec.evidence_sources,
                                                evidence_warnings: rec.evidence_warnings }}
                                        featureName="AI Supplement Suggestions" groupTitle="Evidence for this supplement"
                                        testId="supp-citations" warnTestId="supp-warnings" noWarnTestId="supp-no-warnings" />
                      {rec.has_interaction && rec.interaction_details && (
                        <div className="med-recInteractionAlert">
                          {rec.interaction_details.map((ix, i) => (
                            <div key={i}>
                              <p>{severityIcon(ix.severity)} Interaction with <strong>{ix.medication}</strong>: {ix.mechanism}</p>
                              <CitationChip sources={ix.sources} featureName="Medication Interaction Check"
                                            itemTitle={`${rec.supplement_name} + ${ix.medication}`} />
                            </div>
                          ))}
                        </div>
                      )}
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handleAddFromRec(rec)}
                        style={{marginTop: '0.5rem'}}
                      >
                        Add to My Supplements
                      </button>
                    </div>
                  ))}
                  {aiRecsPromptData && (
                    /* featureName is REQUIRED, not decorative: it selects the baseline citation
                       set in xaiHelpers. Without it this fell through to `general` and cited
                       dietary + physical-activity guidance on a SUPPLEMENT screen — accurate
                       documents, wrong subject, and the first surface an App Review tester with
                       prescriptions on file would open (Apple 1.4.1). */
                    <XaiPromptPanel
                      promptData={aiRecsPromptData}
                      featureName="AI Supplement Suggestions"
                    />
                  )}
                  <MedicalDisclaimer variant="ai" />
                </>
              ) : (
                <p>No recommendations generated.{aiRecsNote ? ` ${aiRecsNote}` : ''}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {showPrescriptionForm && (
        <PrescriptionForm
          onClose={closePrescriptionForm}
          onSave={loadPrescriptions}
          fetchWithAuth={fetchWithAuth}
          editingPrescription={editingPrescription}
          defaultItemType={defaultItemType}
          prefillData={prefillData}
        />
      )}
      </div>
    </div>
  );
}

export default MedicationsPage;
